import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUrlParamsFetch, handleAxiosError } from "@/shared";
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from "@/shared/constants";
import { AlertSeverityEnum, TiketUsers, TiketUsersPagingRes, UseMutationsParams } from "@/shared/interfaces";
import { useUiStore } from "@/store/ui";

const { get, post, put } = erpApi();

///* tanSctack query
export const usefetchTiketUsers = (params?: GetTiketUsersParams) => {
  return useQuery({
    queryKey: ["TiketUsers", ...Object.values(params || {})],
    queryFn: async () => {
      return getTiketUsers(params);
    },
  });
};

export const useGetTiketUser = (id: string) => {
  return useQuery({
    queryKey: ["TiketUsers", id],
    queryFn: async () => {
      return getTiketUser(id);
    },
  });
};

export const useCreateTiketUser = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTiketUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TiketUsers'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ticket creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTiketUser = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTiketUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiketUsuario'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ticket actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveTiketsUsers = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  return useMutation({
    mutationFn: deleteTiketUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TiketUsers"] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type GetTiketUsersParams = {
  page?: number;
  page_size?: number;
  /// search
  nombre_requerimento?: string;
  estado_ticket?: string;
};

export type CreateTiketUserParams = Omit<TiketUsers, "id_tiket_user">;
export type UpdateTiketUserParams = {
  id: string;
  data: Omit<TiketUsers, "id_tiket_user">;
};

export const getTiketUsers = async (params?: GetTiketUsersParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<TiketUsersPagingRes>(
    `${urlBackend}api/v1/ticket-user/?${queryParams}`,
    true
  );
};

export const getTiketUser = async (id: string) => {
  try{
    return await get<TiketUsers>(
    `${urlBackend}api/v1/ticket-user/${id}/`,
    true
  );
}
catch(error){
  return handleAxiosError(error);
}
};

export const createTiketUser = async (data: CreateTiketUserParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<TiketUsers>(`${urlBackend}api/v1/ticket-user/`, data, true);
};

export const updateTiketUser = async ({ id, data }: UpdateTiketUserParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<TiketUsers>(`${urlBackend}api/v1/ticket-user/${id}/`, data, true);
};

export const deleteTiketUsers = async ({ id, data }: UpdateTiketUserParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TiketUsers>(
    `${urlBackend}api/v1/ticket-user/${id}/`,
    data,
    true
  );
  setAlert(true, res.message, res.status);
  return res;
};
